// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module defines the core types and schemas for account-related data structures.
 * It includes definitions for organizations, persons, and notification configurations.
 */
import * as z from 'zod';
import { oauthScopeSchema } from '@/account/api/get-connected-applications';
import { redactedFieldSchema, responseMetadataSchema } from '@/api/types';
import {
  type AttentionReason,
  attentionReasonSchema,
} from '@/constants/attention';
import { caseInsensitiveLowerEnum } from '@/constants/case-insensitive-enum';
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import { currencySchema } from '@/currency/api/types';
import { paymentTermSchema } from '@/payment-term/api/types';
import {
  sparseLocationWithParentSchema,
  sparsePersonSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { integrationListSchema } from '@/utilities/integration';
import { IANAZone } from 'luxon';

/**
 * Schema defining the valid HubSpot page types.
 * These page types represent the key contexts within the HubSpot CRM system.
 * HubsSpotPages are different from object types as a page can contain multiple object types.
 */
const crmConfigurationPageSchema = z.enum([
  'PIPELINE', // This is now virtualized on the FE, the BE should not be sending this
  'PROJECT',
  'COMPANY',
  'PRODUCT',
]);
export type CRMConfigurationPage = z.infer<typeof crmConfigurationPageSchema>;

const crmConfigurationStepSchema = z.object({
  id: crmConfigurationPageSchema,
  completed: z.boolean(),
});

type CRMConfigurationStep = z.infer<typeof crmConfigurationStepSchema>;

/**
 * Schema for validating and transforming timezone strings into IANAZone objects.
 * Ensures the timezone is a valid IANA timezone identifier.
 */
const timezoneSchema = z.string().transform((value, context) => {
  try {
    return IANAZone.create(value);
  } catch (_) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid timezone',
    });
  }

  return z.NEVER;
});

/**
 * Schema defining an API key structure.
 */
export const apiKeySchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  scopes: z.array(oauthScopeSchema),
});

/**
 * Schema defining the available subscription plan types
 */
const planTypeSchema = z.enum(['FREE', 'PAID']);

/** Type representing a validated plan type */
export type PlanType = z.infer<typeof planTypeSchema>;

const hubspotConfiguredPipelineSchema = z.object({
  pipelineId: z.string(),
  stageId: z.string(),
  pipelineLabel: z.string(),
  stageLabel: z.string(),
  isArchived: z.boolean(),
  isEnabled: z.boolean(),
  enabledAt: dateTimeSchema.nullable(),
  lastSyncedAt: dateTimeSchema.nullable(),
  startDate: dateSchema.nullable(),
});

const hubspotConfigurationStateSchema = z.object({
  allScopesPresent: z.boolean(),
  steps: z.array(crmConfigurationStepSchema),
  configuredPipelines: z.array(hubspotConfiguredPipelineSchema),
  lastSyncedAt: dateTimeSchema.nullable(),
});

type HubspotConfigurationState = Record<
  CRMConfigurationPage,
  CRMConfigurationStep
>;

const hubspotConfigurationRecordSchema =
  hubspotConfigurationStateSchema.transform((record) => {
    return {
      ...record,
      steps: record.steps.reduce<HubspotConfigurationState>(
        (acc, step) => {
          acc[step.id] = step;
          return acc;
        },
        // Map any missing default entities to false if they are not reported
        {
          PIPELINE: {
            id: 'PIPELINE',
            completed: record.configuredPipelines.length > 0,
          },
          PROJECT: { id: 'PROJECT', completed: false },
          COMPANY: { id: 'COMPANY', completed: false },
          PRODUCT: { id: 'PRODUCT', completed: false },
        }
      ),
    };
  });

const hubspotDealStageSchema = z.object({
  id: z.string(),
  label: z.string(),
});

const hubspotPipelineConfigurationSchema = z.object({
  id: z.string(),
  label: z.string(),
  stages: z.array(hubspotDealStageSchema),
});

export const hubspotConfigurationResponseSchema = z.object({
  data: z.object({
    pipelines: z.array(hubspotPipelineConfigurationSchema),
    configuredPipelines: z.array(hubspotConfiguredPipelineSchema),
  }),
  metadata: responseMetadataSchema,
});

export type HubspotConfiguration = z.infer<
  typeof hubspotConfigurationResponseSchema
>['data'];

/** Schema defining a valid CRM settings */
const hubspotConfigurationSchema = z.object({
  type: z.literal('HUBSPOT'),
  id: z.string(),
  name: z.string(),
  createdAt: dateTimeSchema,
  configurationState: hubspotConfigurationRecordSchema,
});

/** Schema defining Salesforce configuration steps */
const salesforceConfigurationStepSchema = z.object({
  id: crmConfigurationPageSchema,
  completed: z.boolean(),
});

type SalesforceConfigurationStep = z.infer<
  typeof salesforceConfigurationStepSchema
>;

type SalesforceConfigurationState = Record<
  CRMConfigurationPage,
  SalesforceConfigurationStep
>;

/** Schema defining Salesforce integration configuration state */
const salesforceConfigurationStateSchema = z.object({
  enabled: z.boolean(),
  connected: z.boolean(),
  integrationDriver: z.string().nullable(),
  instanceUrl: z.string().nullable(),
  connectedAt: dateTimeSchema.nullable(),
  allScopesPresent: z.boolean(),
  lastSyncedAt: dateTimeSchema.nullable(),
  steps: z.array(salesforceConfigurationStepSchema),
});

const salesforceConfigurationRecordSchema =
  salesforceConfigurationStateSchema.transform((record) => {
    return {
      ...record,
      steps: record.steps.reduce<SalesforceConfigurationState>(
        (acc, step) => {
          acc[step.id] = step;
          return acc;
        },
        // Map any missing default entities to false if they are not reported
        {
          PIPELINE: { id: 'PIPELINE', completed: false },
          PROJECT: { id: 'PROJECT', completed: false },
          COMPANY: { id: 'COMPANY', completed: false },
          PRODUCT: { id: 'PRODUCT', completed: false },
        }
      ),
    };
  });

const sfdcConfigurationSchema = z.object({
  type: z.literal('SFDC'),
  id: z.string(),
  name: z.string(),
  createdAt: dateTimeSchema,
  configurationState: salesforceConfigurationRecordSchema,
});

/** Type representing a validated CRM configuration (HubSpot or Salesforce) */
export type CRMConfiguration =
  | z.infer<typeof hubspotConfigurationSchema>
  | z.infer<typeof sfdcConfigurationSchema>;

/** Schema defining QuickBooks-specific accounting state */
const quickbooksAccountingStateSchema = z.object({
  connected: z.boolean(),
  realmId: z.string().nullable(),
  companyName: z.string().nullable(),
  allScopesPresent: z.boolean(),
  lastSyncedAt: dateTimeSchema.nullable(),
});

/** Schema for QuickBooks accounting configuration */
const quickbooksAccountingConfigurationSchema = z.object({
  type: z.literal('QBO'),
  id: z.string(),
  name: z.string(),
  createdAt: dateTimeSchema,
  configurationState: quickbooksAccountingStateSchema,
});

/** Unified integration configuration discriminated union (CRM + accounting) */
const integrationConfigurationSchema = z.discriminatedUnion('type', [
  hubspotConfigurationSchema,
  sfdcConfigurationSchema,
  quickbooksAccountingConfigurationSchema,
]);

/**
 * The named shapes a serial may take. Mirrors the backend's
 * enums.SerialPatternKind.
 */
export const SERIAL_PATTERN_KINDS = [
  'alpha',
  'alphanumeric',
  'numeric',
  'hex',
  'mac_address',
  'uuid',
] as const;

export const serialPatternKindSchema =
  caseInsensitiveLowerEnum(SERIAL_PATTERN_KINDS);

export type SerialPatternKind = z.infer<typeof serialPatternKindSchema>;

/** Narrows the value MUI hands a Select's renderValue callback. */
export function isSerialPatternKind(
  value: unknown
): value is SerialPatternKind {
  return serialPatternKindSchema.safeParse(value).success;
}

/** One configured serial shape. `length` is null for the fixed-format kinds. */
export const serialPatternSchema = z.object({
  kind: serialPatternKindSchema,
  length: z.number().int().nullable(),
  prefix: z.string().nullable(),
});

export type SerialPattern = z.infer<typeof serialPatternSchema>;

/**
 * Schema defining a person's basic information structure.
 * Includes core identifying information and name details.
 */
export const personSchema = sparsePersonSchema.extend({
  primaryEmail: z.string().nullable().optional(),
});

/** Type representing a validated person object */
export type Person = z.infer<typeof personSchema>;

/**
 * Schema defining organization member information.
 * Extends the basic person schema with organization-specific fields.
 */
export const organizationUserSchema = personSchema.extend({
  email: z.string().nullable(),
  isAdmin: z.boolean(),
  lastAdminStatusChangeAt: dateTimeSchema,
  isDeactivated: z.boolean(),
  lastDeactivationChangeAt: dateTimeSchema,
  userGroup: redactedFieldSchema(z.string()),
  defaultRate: redactedFieldSchema(currencySchema.nullable()),
});

/** Type representing a validated organization member object */
export type OrganizationUser = z.infer<typeof organizationUserSchema>;

/** The subscription phases the backend reports. Mirrors SubscriptionPhase in Go. */
export const SUBSCRIPTION_PHASES = ['active', 'grace', 'ended'] as const;

export const subscriptionPhaseSchema =
  caseInsensitiveLowerEnum(SUBSCRIPTION_PHASES);

export const sparseOrganizationSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  timezone: timezoneSchema,
  logoFileKey: z.string().nullable(),
  iconFileKey: z.string().nullable(),
  assetMarker: z.string().nullable().optional(),
  billingAddress: z.string().nullable(),
  defaultPaymentTerm: paymentTermSchema.nullable(),
  defaultInvoiceMemo: z.string().nullable(),
  defaultPaymentInstructions: z.string().nullable(),
  invoiceNumberPrefix: z.string().nullable(),
  // The backend's shopspring/decimal serializes as a string. Backend column is
  // NOT NULL with a 0.12 default, so the field is always present and never null.
  weightedAverageCostOfCapital: z.string(),
  // Backend column is NOT NULL with a USD default, so the field is always
  // present and never null.
  defaultCurrency: orgDefaultCurrencyCodeSchema,
  defaultOriginLocation: sparseLocationWithParentSchema.nullable(),
  defaultReturnLocation: sparseLocationWithParentSchema.nullable(),
  defaultReceiveLocation: sparseLocationWithParentSchema.nullable(),
  plan: planTypeSchema,
  // The last day of the organization's subscription. A later organization-local
  // date means the subscription has expired and the organization is in its grace
  // period.
  subscriptionEndDate: dateSchema.nullable(),
  // The first day the organization has no access. Lockout starts at midnight
  // on this date in the organization's timezone.
  accessEndDate: dateSchema.nullable(),
  // Where the organization sits relative to those dates, as resolved by the
  // backend in the organization's own timezone
  subscriptionPhase: subscriptionPhaseSchema,
  // The person Hardfin has registered as the contract contact, when one has
  // been recorded.
  contractContact: organizationUserSchema.nullable(),
  // The person Hardfin has registered as the billing contact, when one has
  // been recorded.
  billingContact: organizationUserSchema.nullable(),
  usesThirdPartyFulfillment: z.boolean(),
  // The backend builds this slice with make(), so it arrives as [] rather than
  // null; the coalesce covers an organization serialized before that was true.
  serialPatterns: z
    .array(serialPatternSchema)
    .nullable()
    .transform((patterns) => patterns ?? []),
});

/** Type representing a validated organization object */
export type SparseOrganization = z.infer<typeof sparseOrganizationSchema>;

export const organizationSchema = sparseOrganizationSchema.extend({
  // All connected integration configurations (CRM, accounting, etc.)
  integrationConfigurations: z.array(integrationConfigurationSchema).catch([]),
  availableIntegrations: integrationListSchema,
  enabledIntegrations: integrationListSchema,
});

export const organizationResponseSchema = z.object({
  data: organizationSchema,
  metadata: responseMetadataSchema,
});

export type Organization = z.infer<typeof organizationSchema>;

/**
 * Schema defining the valid HubSpot object types.
 * These object types represent the key entities within the HubSpot CRM system.
 */
const hubspotObjectTypeSchema = z.enum(['DEAL', 'COMPANY', 'LINE_ITEM']);
export type HubspotObjectType = z.infer<typeof hubspotObjectTypeSchema>;

const hubspotPropertyRecordSchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string(),
  sortOrder: z.number().nullable(),
  category: z.string(),
  categoryOrder: z.number().nullable(),
  categoryLabel: z.string(),
});

export type HubspotPropertyRecord = z.infer<typeof hubspotPropertyRecordSchema>;

const hubspotPropertyResponseSchema = z.record(
  hubspotObjectTypeSchema,
  z.array(hubspotPropertyRecordSchema).transform((properties) =>
    [...properties].sort((a, b) => {
      // First, sort by categoryOrder, handling null values
      if (a.categoryOrder === null && b.categoryOrder === null) {
        // If both are null, move to category label comparison
      } else if (a.categoryOrder === null)
        return 1; // Move null to bottom
      else if (b.categoryOrder === null)
        return -1; // Move null to bottom
      else if (a.categoryOrder !== b.categoryOrder) {
        return a.categoryOrder - b.categoryOrder;
      }

      // Then sort by category label alphabetically
      const categoryComparison = a.categoryLabel.localeCompare(b.categoryLabel);
      if (categoryComparison !== 0) return categoryComparison;

      // Then sort by sortOrder, handling null values
      if (a.sortOrder === null && b.sortOrder === null) {
        // If both are null, move to label comparison
      } else if (a.sortOrder === null)
        return 1; // Move null to bottom
      else if (b.sortOrder === null)
        return -1; // Move null to bottom
      else if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }

      // Finally, sort by label alphabetically
      return a.label.localeCompare(b.label);
    })
  )
);

const hubspotPropertyValueSchema = z.object({
  propertyId: z.string(),
  propertyLabel: z.string(),
  optional: z.boolean(),
  wasSet: z.boolean(),
  value: z.string().nullable(),
  defaultValue: z.string().nullable(),
});

const hubspotPropertyConfigurationSchema = z.object({
  properties: hubspotPropertyResponseSchema,
  mappings: z.record(
    hubspotObjectTypeSchema,
    z.array(hubspotPropertyValueSchema)
  ),
});
export const hubspotPropertyConfigurationResponseSchema = z.object({
  data: hubspotPropertyConfigurationSchema,
  metadata: responseMetadataSchema,
});

export type HubspotPropertyConfiguration = z.infer<
  typeof hubspotPropertyConfigurationSchema
>;

/**
 * Schema defining the valid user status values for filtering
 */
export const userStatusSchema = z.enum(['active', 'deactivated']);

/** Type representing a validated user status */
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * Maps user status values to their display labels
 */
export const userStatusLabelMap: Record<UserStatus, string> = {
  active: 'Active',
  deactivated: 'Deactivated',
};

/**
 * Schema defining the valid user role values for filtering
 */
export const userRoleSchema = z.enum(['admin', 'user']);

/** Type representing a validated user role */
export type UserRole = z.infer<typeof userRoleSchema>;

/**
 * Maps user role values to their display labels
 */
export const userRoleLabelMap: Record<UserRole, string> = {
  admin: 'Admin',
  user: 'User',
};

/**
 * Schema defining the valid ledger account types.
 * These represent the different types of accounts used for tracking inventory
 * and fixed assets throughout their lifecycle.
 */
export const ledgerAccountTypeSchema = z.enum([
  'INV_INVENTORY',
  'INV_CLEARING',
  'INV_COGS',
  'INV_WRITE_OFF',
  'FA_FIXED_ASSETS',
  'FA_CAPITALIZED_DEPLOYMENT',
  'FA_DEPRECIATION',
  'FA_ACCUMULATED_DEPRECIATION',
  'FA_DISPOSAL_CLEARING',
  'FA_DISPOSAL_GAIN',
  'FA_DISPOSAL_LOSS',
  'ONBOARDING_ADJUSTMENT',
]);

/** Type representing a validated ledger account type */
export type LedgerAccountType = z.infer<typeof ledgerAccountTypeSchema>;

/**
 * Schema defining the structure of ledger account record data.
 * Each ledger account record type maps to a nullable string representing the account identifier.
 */
export const ledgerAccountRecordSchema = z.record(
  ledgerAccountTypeSchema,
  z.string().nullable()
);

/**
 * Schema defining the structure of ledger accounts data.
 * Each account type maps to a nullable string representing the account identifier.
 */
export const ledgerAccountsSchema = z.object({
  ledgerAccounts: ledgerAccountRecordSchema,
});

/** Type representing validated ledger accounts data */
export type LedgerAccounts = z.infer<typeof ledgerAccountsSchema>;

/** Entity types that can have configurable attention thresholds */
const attentionThresholdEntities = [
  'outbound_shipment',
  'inbound_shipment',
] as const;

/** Schema for an entity type with configurable attention thresholds */
export const attentionThresholdEntitySchema = caseInsensitiveLowerEnum(
  attentionThresholdEntities
);

export type AttentionThresholdEntity = z.infer<
  typeof attentionThresholdEntitySchema
>;

/**
 * Schema for the notice/warning/critical day counts of one reason.
 */
const attentionThresholdTiersSchema = z.object({
  noticeDays: z.number().int().nonnegative(),
  warningDays: z.number().int().nonnegative(),
  criticalDays: z.number().int().nonnegative(),
});

/**
 * Schema for one configurable reason: its shipped default day counts and
 * the organization's override, when one is stored.
 */
const attentionThresholdSettingSchema = z.object({
  entity: attentionThresholdEntitySchema,
  reason: attentionReasonSchema,
  default: attentionThresholdTiersSchema,
  override: attentionThresholdTiersSchema.nullable(),
});

export type AttentionThresholdSetting = z.infer<
  typeof attentionThresholdSettingSchema
>;

/**
 * Schema for the full threshold settings response, in display order.
 */
export const attentionThresholdsSchema = z.object({
  thresholds: z.array(attentionThresholdSettingSchema),
});

export type AttentionThresholds = z.infer<typeof attentionThresholdsSchema>;

/**
 * One reason's override day counts for an update request.
 */
interface AttentionThresholdOverrideInput {
  entity: AttentionThresholdEntity;
  reason: AttentionReason;
  noticeDays: number;
  warningDays: number;
  criticalDays: number;
}

/**
 * The full-replace update request: reasons present are stored as overrides and
 * reasons absent revert to the shipped default.
 */
export interface UpdateAttentionThresholdsInput {
  thresholds: AttentionThresholdOverrideInput[];
}
